import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Paper, Container, Grid, Typography, Button, Dialog, DialogContent, DialogActions, CircularProgress, DialogTitle, DialogContentText, Tooltip, IconButton, FormControlLabel, Checkbox, FormGroup, List, Alert, Snackbar, Box, Chip } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ZonaHorariaDialog from "./dialogs/ZonaHorariaDialog";
import JornadaService from "../../services/JornadaService";
import { avisoConAccionSx } from "../jornada/temaJornada";
import { IEstadoJornada, IHorarioAgenda } from "../../types/Jornada";
import { tramosEnMinutos, textoNoDisponible } from "../../utils/Jornada";
import ILog from "../../types/Log";
import IEvent from "../../types/Event";
import LogService from "../../services/LogService";
import { Calendar, momentLocalizer, View } from "react-big-calendar";
import 'react-big-calendar/lib/css/react-big-calendar.css'
import moment from "moment";
import { esFechaValida } from '../../utils/FechaSelector';
import * as constantes from "../../common/Constantes";
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import FilterOn from '@mui/icons-material/FilterList';
import FilterOff from '@mui/icons-material/FilterListOff';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';
import { createTheme } from '@mui/material/styles';
import ICita from "../../types/Cita";
import PacienteService from "../../services/PacienteService";
import PacientesUtils from "../../utils/Pacientes";
import AgendaUtils from "../../utils/Agenda";
import IPaciente from "../../types/Paciente";
import AgendaService from "../../services/AgendaService";
import 'moment/dist/locale/es';
import IClass from "../../types/Class";
import ClaseUtils from "../../utils/Clase";
import { isMobile } from "react-device-detect";
import IOutOffice from "../../types/OutOffice";
import ConfigUtils from "../../utils/Config";
import IAlert from "../../types/Alert";
import ISala from "../../types/Sala";
import IActividad from "../../types/Actividad";
import IHoliday from "../../types/Holiday";
import EmailIcon from '@mui/icons-material/Email';
import WavesIcon from '@mui/icons-material/Waves';
import LandscapeIcon from '@mui/icons-material/Landscape';
import DiamondIcon from '@mui/icons-material/Diamond';
import GrassIcon from '@mui/icons-material/Grass';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import PacienteDialog from "./dialogs/PacienteDialog";
import CommentDialog from "./dialogs/CommentDialog";
import ClaseDialog from "./dialogs/ClaseDialog";
import ConflictoDialog from "./dialogs/ConflictoDialog";
import { ConflictoAgenda, comprobaciones, leerConflicto, sinConfirmaciones } from "../../utils/Disponibilidad";
import CitaDialog from "./dialogs/CitaDialog";
import { useAgendaMemo } from "./hooks/useAgendaMemo";
import { useAgendaHandlers } from "./hooks/useAgendaHandlers";
import { ISimpleUser } from "../config/Empleados";
import FacturarCitaDialog from "../facturacion/facturarCita/FacturarCitaDialog";
import EstadoFacturacionIcon, { EstadoFacturacion, getEstadoFacturacion } from "../facturacion/common/EstadoFacturacionIcon";
import IServicio from "../../types/Servicio";
import IMetodoPago from "../../types/MetodoPago";

const localizer = momentLocalizer(moment);

const Agenda = () => {
    const theme = createTheme();
    const history = useNavigate();
    const location = useLocation();

    const sanitarios: ISimpleUser[] = useMemo(() => ConfigUtils.getSanitarios(), []);
    const outOffices: IOutOffice[] = useMemo(() => ConfigUtils.getOutOffices(), []);
    const salas: ISala[] = useMemo(() => ConfigUtils.getSalas(), []);
    const actividades: IActividad[] = useMemo(() => ConfigUtils.getActividades(), []);
    const avisoZona = useMemo(() => AgendaUtils.avisoZonaNavegador(), []);
    // Registro de jornada: recordatorio de fichar en un banner, pedido al entrar en el Calendario (si la ayuda está activada)
    const [recordatorioJornada, setRecordatorioJornada] = useState<string | null>(null);
    useEffect(() => {
        const registro = ConfigUtils.getRegistroJornada();
        if (!registro?.puedeFichar || !registro.ayudas?.banner) return;
        JornadaService.getEstado()
            .then((response: { data: IEstadoJornada }) => setRecordatorioJornada(response.data.recordatorio?.texto ?? null))
            .catch(() => setRecordatorioJornada(null));
    }, []);
    // Horarios de quien ficha, para sombrear lo que queda fuera de horario (pedidos al entrar, con el módulo activo)
    const [horariosAgenda, setHorariosAgenda] = useState<IHorarioAgenda[]>([]);
    useEffect(() => {
        if (!ConfigUtils.getRegistroJornada()?.activo) return;
        JornadaService.getHorariosAgenda()
            .then((response: { data: { personas: IHorarioAgenda[] } }) => setHorariosAgenda(response.data.personas))
            .catch(() => setHorariosAgenda([]));
    }, []);
    const [vista, setVista] = useState<View>(isMobile ? "day" : "work_week");
    const [modalZona, setModalZona] = useState(() => {
        if (!avisoZona) return false;
        try { return sessionStorage.getItem('avisoZonaVisto') !== avisoZona.clave; } catch { return true; }
    });
    const cerrarAvisoZona = () => {
        setModalZona(false);
        try { if (avisoZona) sessionStorage.setItem('avisoZonaVisto', avisoZona.clave); } catch { /* sin sessionStorage: se volverá a avisar */ }
    };
    const servicios: IServicio[] = useMemo(() => ConfigUtils.getServicios(), []);
    const metodosPago: IMetodoPago[] = useMemo(() => ConfigUtils.getMetodosPago(), []);
    
    const [errorPaciente, setErrorPaciente] = useState<string>('');
    const [checkedSanitariosMap, setCheckedSanitariosMap] = useState<Record<string, boolean>>({});

    const [showCitas, setShowCitas] = useState(true);
    const [showClasses, setShowClasses] = useState(true);
    const [shownEvents, setShownEvents] = useState<Array<IEvent>>([]);
    const [showFilters, setShowFilters] = useState(false);
    const [showOutOffice, setShowOutOffice] = useState(true);

    const [holidays, setHolidays] = useState<Array<IHoliday>>(ConfigUtils.getHolidays());

    const [loading, setLoading] = useState(true);
    const [loadingCita, setLoadingCita] = useState(false);
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [events, setEvents] = useState<Array<IEvent>>([]);
    const [pacientes, setPacientes] = useState<Array<IPaciente>>([]);
    const [cita, setCita] = useState<ICita>();
    const [isEditCita, setIsEditCita] = useState(false);
    const [event, setEvent] = useState<IEvent>();
    const [modalEvent, setModalEvent] = useState(false);
    const [modalEventDelete, setModalEventDelete] = useState(false);
    const [modalCita, setModalCita] = useState(false);
    const [start, setStart] = useState<Date>(new Date());
    const [startTime, setStartTime] = useState<Date>(new Date());
    const [endTime, setEndTime] = useState<Date>(new Date());
    const [especialistaId, setEspecialistaId] = useState<number>(0);
    const [salaId, setSalaId] = useState<number>(0);
    const [pacienteId, setPacienteId] = useState<string>('');
    const [repetirId, setRepetirId] = useState<number>(0);
    // Final de hoy, como pone el selector al elegir un día: con la hora actual, el último día se incluía o no según la hora
    const [terminarRepetir, setTerminarRepetir] = useState<Date>(new Date(new Date().setHours(23, 59, 59, 999)));
    const [modalAusencia, setModalAusencia] = useState(false);

    //CONFIRMACION CITA
    const [modalConfirmacion, setModalConfirmacion] = useState(false);
    const [loadingConfirmacion, setLoadinConfirmacion] = useState(false);
    const [citaConfirmacion, setCitaConfirmacion] = useState<ICita>();

    //CLASES
    const [modalClass, setModalClass] = useState(false);
    const [profesorId, setProfesorId] = useState<number>(0);
    const [salaClassId, setSalaClassId] = useState<number>(0);
    const [duracion, setDuracion] = useState<number>(60);
    const [pacientesIds, setPacientesIds] = useState<Array<IPaciente>>([]);
    const [startClass, setStartClass] = useState<Date>(new Date());
    const [loadingClass, setLoadingClass] = useState(false);
    const [modalClassEvent, setModalClassEvent] = useState(false);
    const [modalClassDelete, setModalClassDelete] = useState(false);
    const [modalClassComentarios, setModalClassComentarios] = useState(false);
    const [comentariosClass, setComentariosClass] = useState('');
    const [modifiedComment, setModifiedComment] = useState('');
    
    //PACIENTE
    const [modalPaciente, setModalPaciente] = useState(false);
    const [modalOutOffice, setModalOutOffice] = useState(false);

    //MODAL SALA
    // No disponible del profesional o sala ocupada al guardar: la pantalla pregunta y, si el usuario confirma, reenvía sin
    // esa comprobación (lo ya confirmado se guarda en una ref para que el reintento no use un valor antiguo).
    const [conflicto, setConflicto] = useState<ConflictoAgenda | null>(null);
    const confirmaciones = useRef(sinConfirmaciones());
    // Qué guardado hay que repetir al confirmar: lo anota el propio guardado que recibe el aviso (el evento seleccionado
    // no sirve para decidirlo, porque puede ser el de una clase abierta antes).
    const reintento = useRef<'saveCita' | 'updateCita' | 'updateClass' | null>(null);
    const [citaEdicion] = useState<ICita>(location.state);

    //FACTURACION DE CITAS
    const [modalFacturarCita, setModalFacturarCita] = useState(false);
    const [estadoFacturacionCita, setEstadoFacturacionCita] = useState(EstadoFacturacion.SIN_FACTURA);

    const agendaConfig = ConfigUtils.getConfigAgenda();

    const minTime = useMemo(() => {
      const d = new Date();
      d.setHours(agendaConfig?.workingHours?.start ?? 8, 0, 0);
      return d;
    }, []);
    
    const maxTime = useMemo(() => {
      const d = new Date();
      d.setHours(agendaConfig?.workingHours?.end ?? 22, 0, 0);
      return d;
    }, []);

    const { eventPropGetter, dayPropGetter, messages, calendarViews } = useAgendaMemo({
        sanitarios,
        holidays,
    });

    const { selectCita, closeModalCita, closeModalClass, closeModalEvent, closeModalClassEvent, createCita, nuevaCita } = useAgendaHandlers({
        pacientes,
        actividades,
        isEditCita,
        setEvent,
        setModalClassEvent,
        setModalOutOffice,
        setPacientesIds,
        setProfesorId,
        setSalaClassId,
        setStartClass,
        setDuracion,
        setComentariosClass,
        setModalEvent,
        setCita,
        setIsEditCita,
        setPacienteId,
        setEspecialistaId,
        setSalaId,
        setStart,
        setStartTime,
        setEndTime,
        setRepetirId,
        setEstadoFacturacionCita,
        setTerminarRepetir,
        setErrorPaciente,
        setModalCita,
        setModalClass
    });

    useEffect(() => {

        const currentHolidays = AgendaUtils.fillMissingYearsHolidays(holidays, ConfigUtils.getProvincia());
        if (currentHolidays !== holidays) {
            setHolidays(currentHolidays);
        }

        const promisePacientes = PacienteService.getAll();
        const promiseCitas = AgendaService.getAll();
        const promiseClasses = AgendaService.getAllClasses();

        Promise.all([promiseCitas, promisePacientes, promiseClasses])
            .then((response) => {
                const citas = AgendaUtils.formatCitas(response[0].data, response[1].data);
                const classes = ClaseUtils.formatClasses(response[2].data, actividades);

                const formattedEvents = [...citas, ...classes, ...ConfigUtils.formatEvents(currentHolidays), ...ConfigUtils.formatOutOffice(outOffices)]
                formattedEvents.sort((a, b) => {
                    const especialistaA = a.resource?.especialista || '';
                    const especialistaB = b.resource?.especialista || '';
                    if (especialistaA < especialistaB) return -1;
                    if (especialistaA > especialistaB) return 1;
                    return 0;
                });

                setEvents(formattedEvents);
                setShownEvents(formattedEvents);
                const res = PacientesUtils.sortPacientesAlphabetically(response[1].data);
                setPacientes(res);

                if (citaEdicion) {
                    let e = AgendaUtils.formatCitas([citaEdicion], res);

                    setEvent(e[0]);
                    setModalEvent(true);
                    const selectedCita = AgendaUtils.formatEvent(e[0]);
                    setCita(selectedCita);

                    if (selectedCita === undefined) return;
                    setIsEditCita(true);

                    setPacienteId(selectedCita.pacienteId);
                    setEspecialistaId(selectedCita.especialistaId);
                    setSalaId(selectedCita.salaId);
                    setStart(selectedCita.start);
                    setStartTime(selectedCita.start);
                    setEndTime(selectedCita.end);
                    setRepetirId(selectedCita?.repetirId);
                }

            })
            .catch((e) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'Agenda.Component.useEffect', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setLoading(false);
            });

    }, [setAlert, history, citaEdicion, outOffices, actividades, holidays]);

    const applyFilters = useCallback(() => {
      const activeSanitarios = sanitarios.filter(s => s.activo === true);
      let filteredEvents: IEvent[] = [];
        
      activeSanitarios.forEach((sanitario) => {
        const id = sanitario.internalID.toString();
        if (!checkedSanitariosMap[id]) return; // si está desmarcado, lo ignoramos
    
        // Citas (no actividades)
        if (showCitas) {
          filteredEvents.push(
            ...events.filter(e =>
              e.resource &&
              e.resource.especialista === sanitario.internalID &&
              !ConfigUtils.getActividadesArray(actividades).includes(e.title.toLowerCase())
            )
          );
        }
    
        // Clases (actividades)
        if (showClasses) {
          filteredEvents.push(
            ...events.filter(e =>
              e.resource &&
              e.resource.especialista === sanitario.internalID &&
              ConfigUtils.getActividadesArray(actividades).includes(e.title.toLowerCase())
            )
          );
        }
    
        // OutOffice / No disponible (resource vacío y título con NO DISPONIBLE - nombre)
        if (showOutOffice) {
          filteredEvents.push(
            ...events.filter(e =>
              e.resource &&
              Object.keys(e.resource).length === 0 &&
              e.title.includes(`NO DISPONIBLE - ${sanitario.firstName}`)
            )
          );
        }
      });

      // Profesionales desactivados: ya no tienen filtro propio, pero su historial (eventos ya terminados) se sigue viendo.
      const ahora = new Date();
      const actividadesArr = ConfigUtils.getActividadesArray(actividades);
      sanitarios.filter(s => s.activo !== true).forEach((sanitario) => {
        const pasados = events.filter(e =>
          e.resource &&
          e.resource.especialista === sanitario.internalID &&
          new Date(e.end) < ahora
        );
        if (showCitas) filteredEvents.push(...pasados.filter(e => !actividadesArr.includes(e.title.toLowerCase())));
        if (showClasses) filteredEvents.push(...pasados.filter(e => actividadesArr.includes(e.title.toLowerCase())));
      });

      // Festivos: no pertenecen a ningún especialista (resource: null), se muestran siempre
      filteredEvents.push(...events.filter(e => e.resource === null));

      // Evitar duplicados por id
      const unique = Array.from(new Map(filteredEvents.map(ev => [ev.id, ev])).values());
      setShownEvents(unique);
    }, [events, sanitarios, checkedSanitariosMap, showCitas, showClasses, showOutOffice, actividades]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // Sombreado del fuera de horario: solo con un único profesional marcado, en las vistas con horas (día y semana), si
    // ficha y tiene horario. Los tramos y el festivo se calculan una vez por día (la función se llama en cada franja).
    const sombreado = useMemo(() => {
        if (vista === 'month' || vista === 'agenda') return null;
        const marcados = sanitarios.filter(s => s.activo === true && checkedSanitariosMap[s.internalID.toString()]);
        if (marcados.length !== 1) return null;
        const versiones = horariosAgenda.find(h => h.especialistaId === marcados[0].internalID)?.versiones;
        return versiones?.length ? { nombre: marcados[0].firstName, versiones } : null;
    }, [vista, sanitarios, checkedSanitariosMap, horariosAgenda]);

    const slotPropGetter = useMemo(() => {
        if (!sombreado) return undefined;
        const festivos = ConfigUtils.formatEvents(holidays);
        const porDia = new Map<string, { inicio: number; fin: number }[] | null>();
        return (fecha: Date) => {
            const clave = fecha.toDateString();
            if (!porDia.has(clave)) {
                // En festivo ya hay color de día completo: no se sombrea
                const esFestivo = festivos.some(f => ConfigUtils.isSameLocalDay(f.start, fecha));
                porDia.set(clave, esFestivo ? null : tramosEnMinutos(sombreado.versiones, fecha));
            }
            const tramos = porDia.get(clave);
            if (!tramos) return {};
            const minuto = fecha.getHours() * 60 + fecha.getMinutes();
            return tramos.some(t => minuto >= t.inicio && minuto < t.fin) ? {} : { style: { backgroundColor: constantes.colorFueraHorario } };
        };
    }, [sombreado, holidays]);

    useEffect(() => {
        const active = sanitarios.filter(s => s.activo === true);
        const initial: Record<string, boolean> = {};
        active.forEach(s => {
            initial[s.internalID.toString()] = true;
        });

        setCheckedSanitariosMap(prev => {
            // preserva selecciones previas para sanitarios que siguen existiendo
            const merged = { ...initial };
            Object.keys(prev).forEach(k => {
            if (k in merged) merged[k] = prev[k];
            });
            return merged;
        });
    }, [sanitarios]);

    const combineDateAndTime = (date: Date, time: Date) => {
        const newDate = new Date(date);
        newDate.setHours(new Date (time).getHours());
        newDate.setMinutes(new Date (time).getMinutes());
        return newDate;
    };

    // Fecha u horas vacías o a medio escribir, o la final no posterior a la de inicio: no se envía (llegaban a la API como null:
    // 500 al crear y la cita guardada sin fecha al editar). Devuelve el aviso para el usuario o '' si es correcto.
    // creando: al crear se comprueba también la fecha de terminar la repetición (al editar ese campo está desactivado)
    const errorFechasCita = (creando: boolean): string => {
        if (!esFechaValida(start) || !esFechaValida(startTime) || !esFechaValida(endTime)) return 'Indica una fecha y unas horas de inicio y fin válidas';
        if (combineDateAndTime(start, endTime).getTime() <= combineDateAndTime(start, startTime).getTime()) return 'La hora final debe ser posterior a la de inicio';
        // Cita que se repite: la fecha de terminar la repetición válida y no anterior a la de la cita
        if (creando && repetirId !== 0) {
            if (!esFechaValida(terminarRepetir)) return 'Indica una fecha válida para terminar la repetición';
            if (moment(terminarRepetir).endOf('day').isBefore(moment(start).startOf('day'))) return 'La fecha de terminar la repetición debe ser igual o posterior a la de la cita';
        }
        return '';
    };

    const addEvents = useCallback((newItems: any[]) => {
        setEvents(prev => {
            const updated = [...prev, ...newItems];
            setShownEvents(updated);
            return updated;
        });
    }, []);

    const updateEvents = useCallback(
      (updatedEvents: any[], eventId?: string | number | null) => {
        if (!eventId) return;

        setEvents(prev => {
          const updated = [...prev.filter(e => e.id!.toString() !== eventId), ...updatedEvents];
          setShownEvents(updated);
          return updated;
        });
      },
      []
    );

    const removeEvents = useCallback((ids: (string | number | null | undefined)[]) => {
        setEvents(prev => {
            const updated = prev.filter(e => !ids.includes(e.id));
            setShownEvents(updated);
            return updated;
        });
    }, []);

    const closeModalOutOffice = useCallback(() => {
        setModalOutOffice(false);
        setEvent(undefined);
    }, []);

    const saveCita = useCallback(() => {
        setLoadingCita(true);

        if (!pacienteId) {
            setErrorPaciente('El paciente es obligatorio');
            setLoadingCita(false);
            return;
        }
        const errorFechas = errorFechasCita(true);
        if (errorFechas) {
            setAlert({ open: true, severity: 'warning', msg: errorFechas });
            setLoadingCita(false);
            return;
        }

        const inicio = combineDateAndTime(start, startTime);
        const final = combineDateAndTime(start, endTime);

        const newCita: ICita = {
          pacienteId,
          especialistaId,
          salaId,
          start: inicio,
          end: final,
          repetirId,
          ...(repetirId !== 0 && {
          terminarRepetir: terminarRepetir
            ? (moment.isMoment(terminarRepetir)
                ? terminarRepetir.toDate()
                : terminarRepetir)
            : undefined
        }),
          userId: ConfigUtils.getUserId()
        };

        const serviceCall = repetirId !== 0
            ? AgendaService.createRecurrence(newCita, comprobaciones(confirmaciones.current))
            : AgendaService.create(newCita, comprobaciones(confirmaciones.current));

        serviceCall
            .then((response: any) => {
                setModalCita(false);
                confirmaciones.current = sinConfirmaciones();

                const createdCitas = repetirId !== 0
                    ? AgendaUtils.formatCitas(response.data, pacientes)
                    : AgendaUtils.formatCitas([response.data], pacientes);

                addEvents(createdCitas);

                setAlert({
                    open: true,
                    severity: 'success',
                    msg: repetirId !== 0 ? 'Citas guardadas correctamente' : 'Cita guardada correctamente'
                });

                setEspecialistaId(0);
                setSalaId(0);
                setPacienteId('');
                setRepetirId(0);
                setTerminarRepetir(new Date(new Date().setHours(23, 59, 59, 999)));

                if (!moment(inicio).isBefore(moment(), "day") && !repetirId && pacientes.find(p => p._id === pacienteId)?.authEmail && pacientes.find(p => p._id === pacienteId)?.email) {
                    setModalConfirmacion(true);
                    setCitaConfirmacion(response.data);
                }
            })
            .catch((e: any) => {
                if (e.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else if (leerConflicto(e)) {
                    // Profesional no disponible o sala ocupada: se pregunta si se guarda igualmente
                    reintento.current = 'saveCita';
                    setConflicto(leerConflicto(e));
                } else if (e.response?.status === 400) {
                    confirmaciones.current = sinConfirmaciones();
                    setAlert({
                        open: true,
                        severity: 'error',
                        msg: e.response.data.message
                    });
                } else {
                    confirmaciones.current = sinConfirmaciones();
                    const error: ILog = {
                        method:
                            repetirId !== 0
                                ? 'Agenda.Component.saveCitaRecurrence'
                                : 'Agenda.Component.saveCita',
                        level: 'error',
                        message: e.message,
                        meta: e.response
                    };
                    LogService.logError(error);
                    setAlert({
                        open: true,
                        severity: 'error',
                        msg: e.response?.data?.message || 'Error al guardar la cita'
                    });
                }
            })
            .finally(() => setLoadingCita(false));
    }, [pacienteId, start, startTime, endTime, repetirId, pacientes, especialistaId, salaId, terminarRepetir]);

    const updateCita = useCallback(() => {
        if (!pacienteId) {
            setErrorPaciente('El paciente es obligatorio');
            return;
        }
        if (!event?.id) return;
        const errorFechas = errorFechasCita(false);
        if (errorFechas) {
            setAlert({ open: true, severity: 'warning', msg: errorFechas });
            return;
        }

        setLoadingCita(true);

        const inicio = combineDateAndTime(start, startTime);
        const final = combineDateAndTime(start, endTime);

        const updatedCita: ICita = {
            pacienteId,
            especialistaId,
            salaId,
            start: inicio,
            end: final,
            repetirId,
            motivo: event.motivo,
            valoracion: event.valoracion,
            razonamiento: event.razonamiento,
            tratamiento: event.tratamiento,
            userId: ConfigUtils.getUserId()
        };

        AgendaService.update(event.id.toString(), updatedCita, comprobaciones(confirmaciones.current))
            .then((response: any) => {
                setModalCita(false);
                confirmaciones.current = sinConfirmaciones();
                closeModalEvent();

                const updatedEvent = AgendaUtils.formatCitas([response.data], pacientes);
                updateEvents(updatedEvent, event.id);
                setEvent(updatedEvent[0]);

                setAlert({ open: true, severity: 'success', msg: 'Cita actualizada correctamente' });

                if (!moment(cita?.start).isBefore(moment(), "day") && pacientes.find(p => p._id === pacienteId)?.authEmail && pacientes.find(p => p._id === pacienteId)?.email) {
                    setModalConfirmacion(true);
                    setCitaConfirmacion(response.data);
                }
            })
            .catch((e: any) => {
                if (e.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else if (leerConflicto(e)) {
                    // Profesional no disponible o sala ocupada: se pregunta si se guarda igualmente
                    reintento.current = 'updateCita';
                    setConflicto(leerConflicto(e));
                } else if (e.response?.status === 400) {
                    confirmaciones.current = sinConfirmaciones();
                    setAlert({
                        open: true,
                        severity: 'error',
                        msg: e.response.data.message
                    });
                } else {
                    confirmaciones.current = sinConfirmaciones();
                    const error: ILog = {
                        method: 'Agenda.Component.updateCita',
                        level: 'error',
                        message: e.message,
                        meta: e.response
                    };
                    LogService.logError(error);
                    setAlert({
                        open: true,
                        severity: 'error',
                        msg: e.response?.data?.message || 'Error al actualizar la cita'
                    });
                }
            })
            .finally(() => {
                setLoadingCita(false);
            });
    }, [event, pacienteId, start, startTime, endTime, repetirId, pacientes, especialistaId, salaId]);

    const updateClass = useCallback(() => {
        // Fecha u hora vacías o a medio escribir: no se envía (llegaban a la API como null)
        if (!esFechaValida(startClass)) {
            setAlert({ open: true, severity: 'warning', msg: 'Indica una fecha y hora de inicio válidas' });
            return;
        }
        setLoadingClass(true);
        if (!event?.id) return;

        const selectedClass = ClaseUtils.formatClassEvent(event);
        if (!selectedClass) return;

        const updatedClass: IClass = {
            title: actividades.find(a => a.title === selectedClass.title)?.internalID!.toString() || '',
            pacientesIds: pacientesIds.map(p => p._id),
            especialistaId: profesorId,
            salaId: salaClassId,
            start: new Date(startClass),
            end: new Date(new Date(startClass).setMinutes(new Date(startClass).getMinutes() + duracion)),
            repId: selectedClass.repId,
            userId: ConfigUtils.getUserId()
        };

        AgendaService.updateClass(event.id.toString(), updatedClass, comprobaciones(confirmaciones.current))
            .then((response: any) => {
                closeModalClassEvent();
                setModalClass(false);
                confirmaciones.current = sinConfirmaciones();

                const updatedEvent = ClaseUtils.formatClasses([response.data], actividades);
                updateEvents(updatedEvent, event.id);
                setEvent(updatedEvent[0]);

                setAlert({ open: true, severity: 'success', msg: 'Clase actualizada correctamente' });
            })
            .catch((e: any) => {
                if (e.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else if (leerConflicto(e)) {
                    // Profesional no disponible o sala ocupada: se pregunta si se guarda igualmente
                    reintento.current = 'updateClass';
                    setConflicto(leerConflicto(e));
                } else if (e.response?.status === 400) {
                    confirmaciones.current = sinConfirmaciones();
                    setAlert({
                        open: true,
                        severity: 'error',
                        msg: e.response.data.message
                    });
                } else {
                    confirmaciones.current = sinConfirmaciones();
                    const error: ILog = {
                        method: 'Agenda.Component.updateClass',
                        level: 'error',
                        message: e.message,
                        meta: e.response
                    };
                    LogService.logError(error);
                    setAlert({
                        open: true,
                        severity: 'error',
                        msg: e.response?.data?.message || 'Error al actualizar la clase'
                    });
                }
            })
            .finally(() => setLoadingClass(false));
    }, [event, startClass, duracion, actividades, profesorId, salaClassId, pacientesIds]);

    const deleteCita = useCallback(() => {
        if (!event?.id) return;
        AgendaService.deleteCita(event?.id.toString())
            .then((response: any) => {
                setEvents(events.filter(x => x.id !== event.id));
                setShownEvents(events.filter(x => x.id !== event.id));
                setEvent(undefined);
                setAlert({ open: true, severity: 'success', msg: response.data.message });
            })
            .catch((e: any) => {
                if (e.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else if (e.response?.status === 400) {
                    setAlert({
                        open: true,
                        severity: 'error',
                        msg: e.response.data.message
                    });
                } else {
                    const error: ILog = {
                        method: 'Agenda.Component.deleteCita',
                        level: 'error',
                        message: e.message,
                        meta: e.response
                    };
                    LogService.logError(error);
                    setAlert({
                        open: true,
                        severity: 'error',
                        msg: e.response?.data?.message || 'Error al eliminar la cita'
                    });
                }
            })
            .finally(() => {
                setModalEventDelete(false);
                closeModalEvent();
            });
    }, [event]);

    const deleteClase = useCallback(() => {
        if (!event?.id) return;

        AgendaService.deleteClase(event.id.toString())
            .then((response: any) => {
                removeEvents([event.id]);
                setEvent(undefined);
                setAlert({ open: true, severity: 'success', msg: response.data.message });
            })
            .catch((e: any) => {
                if (e.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = {
                        method: 'Agenda.Component.deleteClase',
                        level: 'error',
                        message: e.message,
                        meta: e.response
                    };
                    LogService.logError(error);
                    setAlert({
                        open: true,
                        severity: 'error',
                        msg: e.response?.data?.message || 'Error al eliminar la clase'
                    });
                }
            })
            .finally(() => {
                setModalClassDelete(false);
                closeModalClassEvent();
            });
    }, [event]);

    const deleteCitaRecurrente = useCallback(() => {
        if (!event?.id) return;

        AgendaService.deleteRecurrence(event.id.toString())
            .then((response: any) => {
                const { eliminadas, msg, severity } = response.data;
                removeEvents(eliminadas);
                setEvent(undefined);
                setAlert({ open: true, severity, msg });
            })
            .catch((e: any) => {
                if (e.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = {
                        method: 'Agenda.Component.deleteCitaRecurrente',
                        level: 'error',
                        message: e.message,
                        meta: e.response
                    };
                    LogService.logError(error);
                    setAlert({
                        open: true,
                        severity: 'error',
                        msg: e.response?.data?.message || 'Error al eliminar las citas recurrentes'
                    });
                }
            })
            .finally(() => {
                setModalEventDelete(false);
                closeModalEvent();
            });
    }, [event]);

    const onChangeStartTime = useCallback((event: Date) => {
        const time = new Date(event)
        setStartTime(event);
        // Mientras la hora de inicio está a medio escribir no se toca la final
        if (esFechaValida(event)) setEndTime(new Date(time.setMinutes(time.getMinutes() + 60)));
    }, []);

    const handleToggleSanitario = useCallback((internalID: number | string, checked: boolean) => {
        const id = internalID.toString();
        setCheckedSanitariosMap(prev => ({ ...prev, [id]: checked }));
    }, []);

    const handleCloseAlert = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    }, []);

    const resetFilters = () => {
      setShowCitas(true);
      setShowClasses(true);
      setShowOutOffice(true);
      // marcar todos los sanitarios activos
        const newMap: Record<string, boolean> = {};
        sanitarios.filter(s => s.activo === true).forEach(s => {
            newMap[s.internalID.toString()] = true;
        });
      setCheckedSanitariosMap(newMap);
      setShowFilters(!showFilters);
    };

    const setCitaAusente = useCallback(() => {
        if (!event || !event.id) return;

        AgendaService.setCitaNoAsistida(event.id.toString())
        .then((response: any) => {
            setModalCita(false);
            const updatedEvent = AgendaUtils.formatCitas([response.data], pacientes);
            updateEvents(updatedEvent, event.id);
            setEvent(updatedEvent[0]);
            setAlert({ open: true, severity: 'success', msg: 'Cita actualizada correctamente' });
        })
        .catch((e: any) => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'Agenda.Component.setCitaAusente', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            }
        })
        .finally(() => {
            setModalAusencia(false);
            closeModalEvent();
        });
    }, [event, pacientes, updateEvents, closeModalEvent, setAlert]);

    const sendConfirmacion = useCallback(() => {
        if (!citaConfirmacion) return;

        const paciente = pacientes.find(p => p._id == citaConfirmacion.pacienteId);
        setLoadinConfirmacion(true);

        AgendaService.sendConfirmacion({ email: paciente?.email, cita: citaConfirmacion, paciente, sanitarios, citaTime: AgendaUtils.formatCitaDateTime(citaConfirmacion) })
        .then(response => {
            setAlert({ open: true, severity: 'success', msg: response.data.message });
            setCita(prev =>
              prev
                ? {
                    ...prev,
                    confirmacion: {
                      sent: true,
                      fecha: new Date(),
                    },
                  }
                : prev
            );
            setShownEvents(prev =>
              prev
                ? prev.map(ev =>
                    ev.id === citaConfirmacion._id
                      ? {
                          ...ev,
                          resource: {
                            ...ev.resource,
                            confirmacion: true,
                          },
                        }
                      : ev
                  )
                : prev
            );
            setCitaConfirmacion(undefined);
        })
        .catch (e => {
            if (e.response.status === 401) {
                localStorage.clear();
                history(constantes.R_HOME);
            } else {
                const error: ILog = { method: 'Agenda.Component.sendConfirmacion', level: 'error', message: e.message, meta: e.response };
                LogService.logError(error);
                setAlert({ open: true, severity: 'error', msg: e.response.data.message });
            }
        })
        .finally(() => {
            setModalConfirmacion(false);
            setLoadinConfirmacion(false);
        });
    }, [citaConfirmacion, pacientes, sanitarios, setAlert]);

    const Event: React.FC<{ event: IEvent }> = ({ event }) => {
        const salaId = event?.resource?.sala;
        const sala = salas.find(s => s.internalID == salaId);
        let icon;
    
        if (sala != null) {
            // Asignar el ícono según el valor de sala.icon
            switch (sala.icon) {
                case "0":
                    icon = <WavesIcon sx={{ fontSize: '15px' }} />;
                    break;
                case "1":
                    icon = <DiamondIcon sx={{ fontSize: '15px' }} />;
                    break;
                case "2":
                    icon = <LandscapeIcon sx={{ fontSize: '15px' }} />;
                    break;
                case "3":
                    icon = <GrassIcon sx={{ fontSize: '15px' }} />;
                    break;
                case "4":
                    icon = <AcUnitIcon sx={{ fontSize: '15px' }} />;
                    break;
                default:
                    break;
            }
        }
    
        const esFestivo = event.resource === null;

        return (
            <div>
                {/* Primera línea: hora e ícono (si existe) — los festivos son de día completo, sin horario */}
                {!esFestivo &&
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        <span style={{ fontSize: '80%', marginRight: '5px' }}>
                            {moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}
                        </span>
                        {icon && <span>{icon}</span>}
                    </div>
                }
                {/* Segunda línea: título del evento */}
                <div>{event.title}</div>
            </div>
        );
    };
    
    const facturacion = servicios.length > 0 && metodosPago.length > 0;
    const paciente = pacientes.find(p => p._id == cita?.pacienteId);
    const { isDisabledEmail, tooltipMsg } = PacientesUtils.canSendEmail(
      loadingConfirmacion,
      paciente?.authEmail,
      paciente?.email,
      cita?.confirmacion?.sent === true
        ? "Volver a enviar confirmación"
        : "Enviar confirmación de cita"
    );

    try {
        return (
            <>
                <Grid>
                    <Grid container>
                            <Container>
                                <Paper elevation={3} sx={{ marginTop: theme.spacing(2), padding: theme.spacing(1), marginBottom: theme.spacing(3) }}>
                                    {loading ? 
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        py: 2,
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <CircularProgress color="inherit" size={32} thickness={4} />
                                  
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          mt: 2,
                                          color: 'text.secondary',
                                          letterSpacing: 0.2,
                                        }}
                                      >
                                        Cargando calendario...
                                      </Typography>
                                    </Box>
                                    :
                                        <>
                                            {/* NUEVA CITA: arriba, en la fila de los filtros (en táctil, seleccionar un hueco exige mantener pulsado) */}
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Box sx={{ flex: 1 }}>
                                            {showFilters ?
                                                <>
                                                    <Button color="info" size="small" variant="contained" onClick={resetFilters} startIcon={<FilterOff />}>Quitar filtros</Button>
                                                    <FormGroup aria-label="position" row>
                                                        <FormControlLabel control={<Checkbox size={isMobile ? "small" : "medium"} checked={showOutOffice} onChange={() => { setShowOutOffice(!showOutOffice) }} inputProps={{ 'aria-label': 'controlled' }} />} label="No disponible" />
                                                        {actividades.length !== 0 && <><FormControlLabel control={<Checkbox size={isMobile ? "small" : "medium"} checked={showCitas} onChange={() => { setShowCitas(!showCitas) }} inputProps={{ 'aria-label': 'controlled' }} />} label="Citas" />
                                                        <FormControlLabel control={<Checkbox size={isMobile ? "small" : "medium"} checked={showClasses} onChange={() => { setShowClasses(!showClasses) }} inputProps={{ 'aria-label': 'controlled' }} />} label="Clases" /></>}

                                                        {
                                                            sanitarios.filter(e => e.activo == true).map((empleado: any, index: number) => {
                                                                const id = empleado.internalID.toString();
                                                                return (
                                                                    <FormControlLabel key={empleado.internalID} control={<Checkbox size={isMobile ? "small" : "medium"} style={{ color: empleado.color }} checked={!!checkedSanitariosMap[id]} inputProps={{ 'aria-label': 'controlled' }} />} label={empleado.firstName} onChange={(e, checked) => handleToggleSanitario(empleado.internalID, checked)} />
                                                                );
                                                            })
                                                        }

                                                    </FormGroup>
                                                </> :
                                                <Button sx={{ marginBottom: theme.spacing(1) }} color="info" size="small" variant="contained" onClick={() => setShowFilters(!showFilters)} startIcon={<FilterOn />}>Mostrar filtros</Button>
                                            }
                                            </Box>
                                            {/* Sin partirse en dos líneas en móviles estrechos: cede espacio el lado de los filtros */}
                                            <Button sx={{ whiteSpace: 'nowrap', flexShrink: 0 }} color="info" size="small" variant="contained" onClick={nuevaCita} startIcon={<AddIcon />}>Nueva cita</Button>
                                            </Box>
                                            <style>
                                                {`
                                                    /* Oculta la hora del evento */
                                                    .rbc-event-label {
                                                        display: none;
                                                    }
                                                `}
                                            </style>
                                            {avisoZona &&
                                                <Box sx={{ mb: 1 }}>
                                                    <Chip icon={<ScheduleIcon />} color="warning" variant="outlined" size="small" label={`Tu hora: ${avisoZona.diferencia} que la clínica`} onClick={() => setModalZona(true)} />
                                                </Box>}
                                            <ZonaHorariaDialog open={modalZona} aviso={avisoZona} onClose={cerrarAvisoZona} />
                                            {recordatorioJornada &&
                                                <Alert severity="warning" sx={{ mb: 1, ...avisoConAccionSx }}
                                                    action={<Button color="inherit" size="small" onClick={() => history(constantes.R_JORNADA)}>Ir a Registro de jornada</Button>}>
                                                    {recordatorioJornada}
                                                </Alert>}
                                            {sombreado && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                                    <Box component="span" sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: constantes.colorFueraHorario, border: 1, borderColor: 'divider' }} />
                                                    En gris, fuera del horario de {sombreado.nombre}
                                                </Typography>
                                            )}
                                            <Calendar
                                                selectable
                                                popup
                                                defaultDate={citaEdicion ? new Date(citaEdicion.start) : new Date()}
                                                localizer={localizer}
                                                defaultView={isMobile ? "day" : "work_week"}
                                                onView={setVista}
                                                views={calendarViews}
                                                events={shownEvents}
                                                min={minTime}
                                                max={maxTime}
                                                style={{ height: '95vh', fontSize: '12px', wordBreak: 'break-word' }}
                                                onSelectEvent={selectCita}
                                                onSelectSlot={createCita}
                                                components={{ event: Event }}
                                                messages={messages}
                                                eventPropGetter={eventPropGetter}
                                                dayPropGetter={dayPropGetter}
                                                slotPropGetter={slotPropGetter}
                                                dayLayoutAlgorithm={agendaConfig?.calendarLayout ?? 'no-overlap'}
                                            />
                                        </>}
                                </Paper>
                            </Container>
                    </Grid>

                    {/* AÑADIR CITA */}
                    {modalCita && (
                        <CitaDialog
                            open={modalCita}
                            loading={loadingCita}
                            onSave={saveCita}
                            onUpdate={updateCita}
                            onCancel={closeModalCita}
                            pacientes={pacientes}
                            isEdit={isEditCita}
                            pacienteId={pacienteId}
                            setPacienteId={setPacienteId}
                            errorPaciente={errorPaciente}
                            setErrorPaciente={setErrorPaciente}
                            sanitarios={sanitarios}
                            especialistaId={especialistaId}
                            setEspecialistaId={setEspecialistaId}
                            salas={salas}
                            salaId={salaId}
                            setSalaId={setSalaId}
                            setModalPaciente={setModalPaciente}
                            
                            start={start}
                            setStart={setStart}
                            startTime={startTime}
                            onChangeStartTime={onChangeStartTime}
                            endTime={endTime}
                            setEndTime={setEndTime}
                            repetirId={repetirId}
                            setRepetirId={setRepetirId}
                            terminarRepetir={terminarRepetir}
                            setTerminarRepetir={setTerminarRepetir}
                        />
                    )}

                    {/* SELECCIONAR CITA */}
                    {modalEvent && <Dialog open={modalEvent} maxWidth="lg" onClose={closeModalEvent}>
                        <DialogTitle>{`${event?.title}`}</DialogTitle>
                        <DialogContent>
                            <div>{AgendaUtils.formatDateEvent(event)}</div>
                            <div>{AgendaUtils.formatTimeEvent(event)}</div>
                            <div>
                                {(() => {
                                  const sala = salas.find(s => s.internalID === event?.resource?.sala);
                                  if (!sala) {
                                    return "Sala no encontrada";
                                  }
                              
                                let icon;
                                switch (sala.icon) {
                                  case "0":
                                    icon = <WavesIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                                    break;
                                  case "1":
                                    icon = <DiamondIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                                    break;
                                  case "2":
                                    icon = <LandscapeIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                                    break;
                                  case "3":
                                    icon = <GrassIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                                    break;
                                  case "4":
                                    icon = <AcUnitIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                                    break;
                                  default:
                                    break;
                                }
                            
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {icon}
                                    <span>{sala.title}</span>
                                  </div>
                                );
                              })()}
                            </div>

                        </DialogContent>
                        {event && !event?.allDay && !ConfigUtils.getActividadesArray(actividades).includes(event!.title) &&
                            <DialogActions sx={{ justifyContent: 'space-evenly' }}>
                                <Tooltip title="VER PACIENTE" placement="top">
                                    <IconButton onClick={() => history(`${constantes.R_PACIENTE}?${constantes.QP_ID}=${cita?.pacienteId}`)}>
                                        <VisibilityIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="CAMBIAR CITA" placement="top">
                                    <IconButton onClick={() => setModalCita(true)}>
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                {facturacion && (
                                <EstadoFacturacionIcon
                                  estado={estadoFacturacionCita}
                                  onClick={() => setModalFacturarCita(true)}
                                />
                                )}
                                {
                                    cita?.asistencia !== false && <Tooltip title="SIN ASISTENCIA A LA CITA" placement="top">
                                        <IconButton onClick={() => setModalAusencia(true)}>
                                            <LogoutIcon />
                                        </IconButton>
                                    </Tooltip>
                                }
                                {!moment(cita?.start).isBefore(moment(), "day") && <Tooltip title={tooltipMsg} placement="top">
                                  <span>
                                    <IconButton
                                      onClick={() => {setCitaConfirmacion(cita); setModalConfirmacion(true)}}
                                      disabled={isDisabledEmail}
                                    >
                                      <EmailIcon color={cita?.confirmacion?.sent === true ? "success" : "inherit"} />
                                    </IconButton>
                                  </span>
                                </Tooltip>}
                                <Tooltip title="ELIMINAR CITA" placement="top">
                                    <IconButton onClick={() => setModalEventDelete(true)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </DialogActions>
                        }
                    </Dialog>}

                    {/* SELECCIONAR NO DISPONIBLE */}
                    {modalOutOffice && <Dialog open={modalOutOffice} maxWidth="lg" onClose={closeModalOutOffice}>
                        <DialogTitle>{`${event?.title}`}</DialogTitle>
                        <DialogContent>
                            {/* El periodo en lenguaje claro, como en Configuración: «Lunes 3 nov · todo el día» */}
                            {event?.start && event?.end && <div>{textoNoDisponible(event.start, event.end).texto}</div>}
                        </DialogContent>
                    </Dialog>}

                    {/* BORRAR CITA */}
                    {modalEventDelete && <Dialog open={modalEventDelete} maxWidth="lg" onClose={() => setModalEventDelete(false)}>
                        <DialogTitle>ELIMINAR CITA</DialogTitle>
                        {

                            event?.resource.repetirId !== 0 ?

                                <><DialogContent>
                                    <DialogContentText>¿Está seguro de que desea eliminar la cita? Es una cita recurrente.</DialogContentText>
                                </DialogContent>
                                    <DialogActions>
                                        <Button onClick={() => deleteCita()} color="error">ELIMINAR SOLO ESTA CITA</Button>
                                        <Button onClick={() => deleteCitaRecurrente()} color="error">ELIMINAR TODOS LAS CITAS FUTURAS</Button>
                                        <Button onClick={() => setModalEventDelete(false)} color="primary" autoFocus>NO</Button>
                                    </DialogActions></> :

                                <><DialogContent>
                                    <DialogContentText id="alert-dialog-description">¿Está seguro de que desea eliminar la cita?</DialogContentText>
                                </DialogContent>
                                    <DialogActions>
                                        <Button onClick={() => deleteCita()} color="primary">SI</Button>
                                        <Button onClick={() => setModalEventDelete(false)} color="primary" autoFocus>NO</Button>
                                    </DialogActions></>
                        }

                    </Dialog>}

                    {/* EDITAR CLASE */}
                    {modalClass && (
                        <ClaseDialog
                            open={modalClass}
                            loading={loadingClass}
                            onUpdate={updateClass}
                            onCancel={closeModalClass}
                            event={event}
                            pacientes={pacientes}
                            pacientesIds={pacientesIds}
                            setPacientesIds={setPacientesIds}
                            pacienteId={pacienteId}
                            profesorId={profesorId}
                            setProfesorId={setProfesorId}
                            sanitarios={sanitarios}
                            salas={salas}
                            salaId={salaClassId}
                            setSalaId={setSalaClassId}
                            startClass={startClass}
                            setStartClass={setStartClass}
                            duracion={duracion}
                            setDuracion={setDuracion}
                        />
                    )}

                    {/* SELECCIONAR CLASE */}
                    {modalClassEvent && <Dialog open={modalClassEvent} maxWidth="lg" onClose={closeModalClassEvent}>
                        <DialogTitle>{`${event?.title} - ${sanitarios.filter((s: any) => s.internalID === event?.resource.especialista)[0]?.firstName != undefined ? sanitarios.filter((s: any) => s.internalID === event?.resource.especialista)[0]?.firstName : ''}`}</DialogTitle>
                        <DialogContent>
                            <div>{AgendaUtils.formatDateEvent(event)}</div>
                            <div>{AgendaUtils.formatTimeEvent(event)}</div>
                            <div>
                                {(() => {
                                  const sala = salas.find(s => s.internalID === event?.resource?.sala);
                                  if (!sala) {
                                    return "Sala no encontrada";
                                  }
                              
                                let icon;
                                switch (sala.icon) {
                                  case "0":
                                    icon = <WavesIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                                    break;
                                  case "1":
                                    icon = <DiamondIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                                    break;
                                  case "2":
                                    icon = <LandscapeIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                                    break;
                                  case "3":
                                    icon = <GrassIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                                    break;
                                  case "4":
                                    icon = <AcUnitIcon sx={{ fontSize: '15px', marginRight: '5px' }} />;
                                    break;
                                  default:
                                    break;
                                }
                            
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {icon}
                                    <span>{sala.title}</span>
                                  </div>
                                );
                              })()}
                            </div>
                            <div>
                                <List dense={true}>
                                    <div>
                                        <ul>
                                        {event?.resource.pacientesIds && event?.resource.pacientesIds.map((x: string, index: number) => {
                                            const pacienteClase = pacientes.find(p => p._id === x);

                                            return (
                                                <div key={index}>
                                                    <li>{pacienteClase ? `${pacienteClase.nombre} ${pacienteClase.apellidos}` : 'Paciente eliminado'}</li>
                                                </div>
                                            );
                                        })}
                                        </ul>
                                    </div>
                                </List>
                            </div>
                            {comentariosClass !== '' && <p>{comentariosClass}</p>}
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'space-evenly' }}>
                            <Button onClick={() => { setModalClassComentarios(true); setModifiedComment(comentariosClass) }} color="primary">{`${comentariosClass === '' ? 'AÑADIR COMENTARIOS' : 'ACTUALIZAR COMENTARIOS'}`}</Button>
                            <Button onClick={() => setModalClass(true)} color="primary">EDITAR ESTA CLASE</Button>
                            <Button onClick={() => setModalClassDelete(true)} color="error">BORRAR ESTA CLASE</Button>
                        </DialogActions>
                    </Dialog>}

                    {/* AÑADIR COMENTARIOS A LA CLASE */}
                    {modalClassComentarios && (
                      <CommentDialog
                        open={modalClassComentarios}
                        initialComment={modifiedComment}
                        event={event}
                        actividades={actividades}
                        onCancel={() => {
                          setModalClassComentarios(false);
                          setModifiedComment("");
                          closeModalClassEvent();
                        }}
                        updateEvents={updateEvents}
                        setAlert={setAlert}
                      />
                    )}

                    {/* AÑADIR PACIENTE */}
                    {modalPaciente && (
                      <PacienteDialog
                        open={modalPaciente}
                        onCancel={() => setModalPaciente(false)}
                        pacientes={pacientes}
                        setPacientes={setPacientes}
                        setPacienteId={setPacienteId}
                        setAlert={setAlert}
                      />
                    )}

                    {/* BORRAR CLASE */}
                    {modalClassDelete && <Dialog open={modalClassDelete} maxWidth="lg" onClose={() => setModalClassDelete(false)}>
                        <DialogTitle>ELIMINAR CLASE</DialogTitle>
                        <>
                            <DialogContent>
                                <DialogContentText>¿Está seguro de que desea eliminar la clase?</DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => deleteClase()} color="primary">SI</Button>
                                <Button onClick={() => setModalClassDelete(false)} color="primary" autoFocus>NO</Button>
                            </DialogActions>
                        </>
                    </Dialog>}

                    {/* CITA AUSENTE */}
                    {modalAusencia && <Dialog open={modalAusencia} maxWidth="lg" onClose={() => setModalAusencia(false)}>
                        <DialogTitle>MARCAR CITA COMO NO ASISTIDA</DialogTitle>
                        <>
                            <DialogContent>
                                <DialogContentText>¿Está seguro de que desea marcar la cita como no asistida?</DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={setCitaAusente} color="primary">SI</Button>
                                <Button onClick={() => setModalAusencia(false)} color="primary" autoFocus>NO</Button>
                            </DialogActions>
                        </>
                    </Dialog>}

                    {/* ENVIAR CONFIRMACION */}
                    {modalConfirmacion && <Dialog open={modalConfirmacion} onClose={() => setModalConfirmacion(false)}>
                        <DialogTitle>ENVIAR CONFIRMACION DE CITA</DialogTitle>
                        <DialogContent>
                            <DialogContentText>¿Quieres mandar una confirmación de cita por correo?</DialogContentText>
                        </DialogContent>
                    
                        {loadingConfirmacion ? 
                        <Grid container>
                            <CircularProgress style={{ margin: '20px auto' }} />
                        </Grid> :  
                        <DialogActions>
                            <Button onClick={() => sendConfirmacion()} color="primary">SI</Button>
                            <Button onClick={() => setModalConfirmacion(false)} color="primary" autoFocus>NO</Button>
                        </DialogActions>
                    }
                    </Dialog>}
                
                    {/* PROFESIONAL NO DISPONIBLE O SALA OCUPADA: ¿guardar igualmente? */}
                    <ConflictoDialog
                        conflicto={conflicto}
                        onConfirmar={() => {
                            if (conflicto) confirmaciones.current = { ...confirmaciones.current, [conflicto.tipo]: true };
                            setConflicto(null);
                            if (reintento.current === 'updateClass') updateClass();
                            else if (reintento.current === 'updateCita') updateCita();
                            else if (reintento.current === 'saveCita') saveCita();
                            reintento.current = null;
                        }}
                        onCancelar={() => { confirmaciones.current = sinConfirmaciones(); reintento.current = null; setConflicto(null); }}
                    />

                    {/* FACTURAR CITA */}
                    {modalFacturarCita && 
                        <FacturarCitaDialog 
                            open={modalFacturarCita} 
                            tipo="activa"
                            onClose={() => setModalFacturarCita(false)} 
                            citaId={event?.id?.toString()!}
                            pacienteId={event?.pacienteId!}
                            onServicioActualizado={(updated) => {
                              setEstadoFacturacionCita(updated.estado);
                              if (!updated.sinAviso) setAlert({ open: true, severity: "success", msg: "Factura actualizada correctamente" });
                            }}
                            onFacturaRectificada={(factura) => {
                              setEstadoFacturacionCita(getEstadoFacturacion(factura.estado, factura.estadoPago));
                              setAlert({ open: true, severity: "success", msg: "Factura rectificada correctamente" });
                            }}
                            onRectificativaActualizada={(updated) => {
                              setEstadoFacturacionCita(updated.estado);
                              setAlert({ open: true, severity: "success", msg: "Factura actualizada correctamente" });
                            }}
                            onAnularFactura={() => {
                                setEstadoFacturacionCita(EstadoFacturacion.ANULADA);
                            }}
                            onEntidadCambiada={(_facturaAnuladaId, facturaNueva) => {
                              setEstadoFacturacionCita(getEstadoFacturacion(facturaNueva.estado, facturaNueva.estadoPago));
                            }}
                        />
                    }
                </Grid>

                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                    <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </>
        );
    } catch (e: any) {
        const error: ILog = { method: 'Agenda.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }

};

export default Agenda;